import { useCallback, useEffect, useMemo, useState } from 'react'
import { DropdownOption } from '@/components/dropdown'
import { useLazyQuery, useQuery } from '@apollo/client'
import { Query, SpecialtyType } from '@/lib/generated/graphql-types'
import { GET_USER_PROFILE } from '@/modules/user/profile/queries'
import { SEARCH_SPECIALTY } from '@/modules/surveys/queries'
import { matchSorter } from 'match-sorter'
import {
  professionSlugsToIds,
  PHYSICIAN_SLUG,
} from '@/modules/surveys/configs/onboarding/personal-info-config'

interface UseSpecialtySearchArgs {
  overrideProfessionSlug?: string | null
  overrideProfessionId?: string | null
  onSearchSpecialty?: (specialties: SpecialtyType[]) => void
  isAuthenticated: boolean
  isLoading?: boolean
}

export interface SpecialtyOption extends DropdownOption {
  slug: string
}
const DEFAULT_SPECIALTY_OPTIONS: SpecialtyOption[] = []

export function useSpecialtySearch(args?: UseSpecialtySearchArgs): {
  options: SpecialtyOption[]
  searchSpecialty: (input: string) => SpecialtyOption[]
  getBaseSpecialty: (specialtyId: string) => SpecialtyType | null
  getSpecialtyBySlug: (slug: string) => SpecialtyType | null | undefined
  loading: boolean
} {
  const { data: getUserProfileData } = useQuery<Pick<Query, 'getUserProfile'>>(
    GET_USER_PROFILE,
    {
      skip: !args?.isAuthenticated,
    },
  )
  /**
   * If user authenticated use profile professionId or fallback to physician if not set,
   * for unauthenticated users use physician professionId always.
   */
  const professionSlug =
    args?.overrideProfessionSlug ||
    getUserProfileData?.getUserProfile?.occupation?.profession.slug ||
    PHYSICIAN_SLUG
  const professionId =
    args?.overrideProfessionId || professionSlugsToIds[professionSlug]

  const [previousProfessionId, setPreviousProfessionId] = useState<
    string | null
  >(null)
  const didProfessionIdChange = previousProfessionId !== professionId

  const [fetchSpecialty, { data, loading }] = useLazyQuery<
    Pick<Query, 'searchSpecialty'>
  >(SEARCH_SPECIALTY, {
    variables: { specialtyInput: { query: '', limit: 200, professionId } },
  })

  useEffect(() => {
    if (!professionId || args?.isLoading) return
    fetchSpecialty()
  }, [args?.isLoading, didProfessionIdChange, fetchSpecialty, professionId])

  useEffect(() => {
    setPreviousProfessionId(professionId)
  }, [professionId])

  useEffect(() => {
    // This is in a useEffect to avoid an infinite loop
    // because every re-render of this component, will trigger
    // the useQuery, and call the onComplete().
    const specialties = data?.searchSpecialty?.results || []
    args?.onSearchSpecialty?.(specialties)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.searchSpecialty?.results])

  const searchSpecialty = (input: string): SpecialtyOption[] => {
    const results: SpecialtyType[] = data?.searchSpecialty?.results || []
    const matchedResults = matchSorter(results, input, {
      keys: [
        (item) => item.fullName,
        (item) => item.aliases ?? '',
        (item) => item.sub ?? '',
      ],
      keepDiacritics: true,
    })
    return matchedResults.map((specialty) => ({
      id: specialty.id,
      label: specialty.fullName,
      slug: specialty.slug,
      data: specialty,
    }))
  }

  const getBaseSpecialty = useCallback(
    (specialtyId: string) => {
      const specialtyOptions = data?.searchSpecialty?.results ?? []
      const specialtyOption = specialtyOptions.find(
        (specialty) =>
          specialty.id === specialtyId || specialty.slug === specialtyId,
      )
      const baseSpecialtyId = specialtyOption?.baseSpecialtyId ?? null
      if (!baseSpecialtyId) return null
      return (
        specialtyOptions.find(
          (specialty) => specialty.id === baseSpecialtyId,
        ) ?? null
      )
    },
    [data?.searchSpecialty?.results],
  )

  const getSpecialtyBySlug = (slug: string) => {
    const specialtyOptions = data?.searchSpecialty?.results ?? []
    return specialtyOptions.find((specialty) => specialty.slug === slug)
  }

  const options = useMemo(() => {
    return (
      data?.searchSpecialty?.results?.map((specialty) => {
        return {
          id: specialty.id,
          label: specialty.fullName,
          slug: specialty.slug,
          data: specialty,
        }
      }) ?? DEFAULT_SPECIALTY_OPTIONS
    )
  }, [data?.searchSpecialty?.results])

  return {
    options,
    searchSpecialty,
    getBaseSpecialty,
    getSpecialtyBySlug,
    loading,
  }
}
